import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Upload, Star, Users } from 'lucide-react';

const HiveShareHeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-20 pb-16 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
      
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* HiveShare Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/c79faca6-99d9-45c3-826d-610a40f2e500/public"
              alt="HiveShare Logo"
              className="h-24 w-auto mx-auto mb-6"
            />
          </motion.div>

          {/* Social Proof Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200 px-4 py-2 text-sm font-medium">
              <Star className="h-4 w-4 mr-2 fill-current" />
              HiveShare by Hivetivity
            </Badge>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Share Class & Lesson Videos{' '}
            <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Instantly.
            </span>
            <br />
            Keep Parents Coming Back.
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Secure, family-managed video sharing for your program.{' '}
            <span className="font-semibold text-gray-800">No ads, no hassle, no tech headaches.</span>
          </motion.p>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex items-center justify-center gap-6 mb-10 text-sm text-gray-500"
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>Trusted by top studios</span>
            </div>
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <span>Upload from phone</span>
            </div>
            <div className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              <span>No ads, ever</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Free Trial
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-blue-200 text-blue-700 hover:bg-blue-50 px-8 py-4 text-lg font-semibold"
            >
              <Play className="h-5 w-5 mr-2" />
              See Demo
            </Button>
          </motion.div>

          {/* Additional Trust Statement */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-6 text-sm text-gray-500"
          >
            💳 No credit card required • ✨ Set up in under 5 minutes • 🔒 Enterprise-grade security
          </motion.p>
        </div>

        {/* Hero Image/Video Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-16 max-w-5xl mx-auto"
        >
          <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-2xl aspect-video flex items-center justify-center">
            <div className="text-center">
              <div className="bg-blue-600 rounded-full p-6 mb-4 inline-block">
                <Play className="h-12 w-12 text-white" />
              </div>
              <p className="text-lg font-semibold text-gray-700">Demo Video Coming Soon</p>
              <p className="text-gray-500">See HiveShare in action</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HiveShareHeroSection;